import React, {Component} from 'react'
import {
  Box,
  Heading,
  Text,
  VStack,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  HStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton,
  List,
  ListItem,
  Avatar
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import './ProblemInfo.css'
import Sidebar from '../Sidebar/Sidebar';
import Navigation from '../../Navigation/Navigation';
import MobileMenu from '../MobileMenu/MobileMenu';
import Footer from '../../../components/Footer/Footer';
import colors from '../../../theme/colors';

class ProblemInfo extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isMobileMenuOpen: false
    };
  }

  getPriorityColor = (level) => {
    switch(level) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  }

  getPriorityIcon = (level) => {
    switch(level) {
      case 'high': return '🚨';
      case 'medium': return '⚠️';
      case 'low': return '📝';
      default: return '📋';
    }
  }

  getPriorityText = (level) => {
    switch(level) {
      case 'high': return 'Hoog';
      case 'medium': return 'Midden';
      case 'low': return 'Laag';
      default: return level;
    }
  }

  formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    return new Date(dateString).toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  handleOpenMobileMenu = () => {
    this.setState({ isMobileMenuOpen: true })
  }

  handleCloseMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false })
  }

  componentWillUnmount() {
    if (this.state.isMobileMenuOpen) {
      this.setState({ isMobileMenuOpen: false })
    }
  }

  render () {
    const problem = this.props.state.selectedProblem;

    if (!problem) {
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Geen probleem geselecteerd</AlertTitle>
          <AlertDescription>
            Er is geen probleem geselecteerd om te bekijken.
          </AlertDescription>
        </Alert>
      );
    }

    const priorityColor = this.getPriorityColor(problem.priority_level);
    const suggestions = problem.suggestions || [];

    return (
      <>
        <Grid templateColumns="repeat(24, 1fr)" bg="gray.50">
          {/* sidebar */}
          <GridItem
            as="aside"
            colSpan={{ base: 0, md: 4, lg: 3 }}
            display={{ base: "none", md: "block" }}
            bg="gray.50"
            minHeight={{ lg: '100vh' }}
            p={0}
          >
            <Sidebar
              getFeed = {this.props.getFeed}
              onRouteChange = {this.props.onRouteChange}
              userVersion = {this.props.state.userVersion}
              onLogout = {this.props.onLogout}
            />
          </GridItem>
          <GridItem
            as="main"
            colSpan={{ base: 24, md: 20, lg: 21 }}
            p="10px"
            display="flex"
            flexDirection="column"
            minHeight="100vh"
          >
            <Navigation
              state = {this.props.state}
              onRouteChange = {this.props.onRouteChange}
              getFeed = {this.props.getFeed}
              onVersionChange = {this.props.onVersionChange}
              onLogout = {this.props.onLogout}
              onOpenFeedback = {this.props.onOpenFeedback}
              onOpenMobileMenu = {this.handleOpenMobileMenu}
            />

            <Box p="40px" flex="1">
              <VStack spacing={6} align="stretch">
                {/* Header with back button */}
                <HStack spacing={{ base: 2, md: 4 }} align="center" mb={4}>
                  <IconButton
                    icon={<ArrowBackIcon />}
                    onClick={() => this.props.onRouteChange('overzicht')}
                    variant="ghost"
                    size={{ base: "md", md: "lg" }}
                    color="gray.600"
                    _hover={{ bg: 'gray.100' }}
                  />
                  <VStack align="start" spacing={1} flex="1">
                    <Heading as="h1" size={{ base: "md", md: "lg" }} color="gray.700">
                      {this.getPriorityIcon(problem.priority_level)} {problem.title}
                    </Heading>
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge
                        colorScheme={priorityColor}
                        variant="solid"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        Urgentie: {this.getPriorityText(problem.priority_level)}
                      </Badge>
                      <Badge colorScheme="orange" variant="subtle" fontSize={{ base: "xs", md: "sm" }}>
                        Gewicht: {problem.weight}
                      </Badge>
                      <Badge colorScheme="blue" variant="subtle" fontSize={{ base: "xs", md: "sm" }}>
                        {problem.weight} {problem.weight === 1 ? 'PERSOON' : 'PERSONEN'}
                      </Badge>
                      <Badge colorScheme="purple" variant="subtle" fontSize={{ base: "xs", md: "sm" }}>
                        {problem.suggestion_count} SUGGESTIE{problem.suggestion_count !== 1 ? 'S' : ''}
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>

                <Divider />

                {/* Problem description */}
                <Card
                  bg="white"
                  shadow="lg"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <CardHeader>
                    <Heading size="md" color="gray.700">
                      📋 Het onderliggende probleem
                    </Heading>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Text color="gray.600" lineHeight="1.8" fontSize="md">
                      {problem.description || 'Geen omschrijving beschikbaar.'}
                    </Text>
                  </CardBody>
                </Card>

                {/* Related suggestions */}
                <Card
                  bg="white"
                  shadow="lg"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <CardHeader>
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color="gray.700">
                        💬 Suggesties over dit probleem
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        Deze suggesties zijn door AI samengevoegd onder dit probleem
                      </Text>
                    </VStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    {suggestions.length > 0 ? (
                      <List spacing={3}>
                        {suggestions.map((suggestion) => (
                          <ListItem
                            key={suggestion.id}
                            className="suggestion-item"
                            p={4}
                            bg="neutral.50"
                            borderRadius="md"
                            borderLeft="4px solid"
                            borderLeftColor={colors.primary.main}
                            _hover={this.props.onSuggestionSelect ? { bg: 'blue.50', cursor: 'pointer' } : {}}
                            onClick={() => {
                              if (this.props.onSuggestionSelect) {
                                this.props.onSuggestionSelect(suggestion);
                              }
                            }}
                          >
                            <HStack spacing={3} align="start">
                              <Avatar size="sm" name={suggestion.username} bg={colors.primary.main} />
                              <VStack align="start" spacing={1} flex="1">
                                <HStack spacing={2} flexWrap="wrap">
                                  <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                                    {suggestion.username}
                                  </Text>
                                  <Text fontSize="xs" color="gray.400">
                                    {this.formatDate(suggestion.created_at)}
                                  </Text>
                                  <Badge
                                    colorScheme={suggestion.status === 'reviewed' ? 'yellow' : 'green'}
                                    fontSize="xs"
                                  >
                                    {suggestion.status === 'submitted' ? 'Ingediend' : 'Bekeken'}
                                  </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                  {suggestion.text}
                                </Text>
                              </VStack>
                            </HStack>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box textAlign="center" py={8} color="gray.500">
                        <Text>Geen suggesties beschikbaar voor dit probleem</Text>
                      </Box>
                    )}
                  </CardBody>
                </Card>

              </VStack>
            </Box>
            <Footer onRouteChange={this.props.onRouteChange} />

          </GridItem>
        </Grid>

        {/* Mobile Menu Drawer */}
        <MobileMenu
          isOpen={this.state.isMobileMenuOpen}
          onClose={this.handleCloseMobileMenu}
          onRouteChange={this.props.onRouteChange}
          userVersion={this.props.state.userVersion}
          resetSuggestionsPage={this.props.resetSuggestionsPage}
        />
      </>
    )
  }
}

export default ProblemInfo
